import { supabase } from '../lib/supabase.js';
import {
  tenantRollupCronTriggerFilterParam,
  type TenantRollupCronTriggerFilter,
} from './tenantRollupCronTriggerFilter.js';

export type EnquiryRecommendationEventType = 'whatsapp_share' | 'admin_copy';

export interface EnquiryRecommendationEventInput {
  organizationId: string;
  enquiryId: string;
  eventType: EnquiryRecommendationEventType;
  recommendationLineCount: number;
  abVariant?: 'A' | 'B' | null;
}

export interface EnquiryRecommendationOrgRollup {
  organization_id: string;
  organization_name: string;
  total_shares: number;
  shares_with_chips: number;
  shares_without_chips: number;
  admin_copies: number;
  variant_b_events: number;
  variant_a_shares?: number;
  variant_b_shares?: number;
  variant_a_attach_rate?: number;
  variant_b_attach_rate?: number;
  attach_rate: number;
  converted_shares?: number;
  conversion_rate?: number;
}

export interface PlatformEnquiryRecommendationRollup {
  lookback_days: number;
  total_shares: number;
  shares_with_chips: number;
  shares_without_chips: number;
  admin_copies: number;
  variant_a_events: number;
  variant_b_events: number;
  variant_a_shares?: number;
  variant_b_shares?: number;
  variant_a_with_chips?: number;
  variant_b_with_chips?: number;
  variant_a_attach_rate?: number;
  variant_b_attach_rate?: number;
  converted_shares?: number;
  conversion_rate?: number;
  orgs: EnquiryRecommendationOrgRollup[];
}

export interface EnquiryRecommendationAnalyticsSummary {
  totalShares: number;
  sharesWithChips: number;
  sharesWithoutChips: number;
  adminCopies: number;
  attachRate: number;
  convertedShares: number;
  conversionRate: number;
  variantAShares: number;
  variantBShares: number;
  variantAAttachRate: number;
  variantBAttachRate: number;
}

export interface EnquiryRecommendationWeeklyBucket {
  week_start: string;
  total_shares: number;
  shares_with_chips: number;
  converted_shares: number;
  attach_rate: number;
  conversion_rate: number;
}

export interface EnquiryRecommendationExportRow {
  id: string;
  organization_id: string;
  organization_name?: string;
  enquiry_id: string;
  event_type: string;
  recommendation_line_count: number;
  ab_variant: string | null;
  enquiry_converted_at?: string | null;
  actor_user_id: string | null;
  created_at: string;
}

export interface AbWinnerEvaluation {
  lookback_days: number;
  eligible: boolean;
  winner: 'A' | 'B' | 'none';
  reason: string;
  recommended_max_items: number;
  variant_a_shares: number;
  variant_b_shares: number;
  variant_a_attach_rate: number;
  variant_b_attach_rate: number;
  min_delta_pct: number;
  min_variant_shares: number;
  variant_a_max_items: number;
  variant_b_max_items: number;
}

export interface BackfillShareConversionsResult {
  updated: number;
  organization_id?: string | null;
}

export interface TenantRollupEmailOptInOrg {
  organization_id: string;
  organization_name: string;
  last_sent_at?: string | null;
  failure_count_this_week?: number;
  has_failure_this_week?: boolean;
  retry_requested_this_week?: boolean;
}

export interface TenantRollupEmailOptInRollup {
  opt_in_count: number;
  failures_this_week_count?: number;
  retry_requests_this_week_count?: number;
  orgs: TenantRollupEmailOptInOrg[];
}

export type EnquiryRollupEmailLogScope = 'all' | 'platform' | 'tenant';

export interface EnquiryRollupEmailLogExportRow {
  log_type: 'platform' | 'tenant';
  id: string;
  organization_id: string | null;
  organization_name: string | null;
  week_start: string;
  sent_at: string;
  recipient_count: number | null;
}

export interface TenantRollupEmailHistoryRow {
  week_start: string;
  sent_at: string;
}

export interface TenantRollupEmailManualResult {
  success?: boolean;
  sent?: number | boolean;
  skipped?: number | boolean;
  failed?: number;
  reason?: string;
  subject?: string;
  html?: string;
  recipient_count?: number;
  preview_only?: boolean;
  week_start?: string;
  missed_only?: boolean;
  error?: string;
}

export interface TenantRollupEmailMissedRollup {
  missed_count: number;
  week_start: string;
}

export interface TenantRollupEmailPendingRetryRollup {
  pending_count: number;
  week_start: string;
}

export interface TenantRollupEmailRetryStatus {
  week_start?: string;
  sent_this_week?: boolean;
  has_failure?: boolean;
  retry_requested_this_week?: boolean;
  awaiting_delivery?: boolean;
  retry_ack_pending?: boolean;
}

export interface TenantRollupEmailResendLogRow {
  id: string;
  organization_id: string;
  organization_name: string;
  week_start: string;
  actor_user_id: string | null;
  sent_at: string;
  recipient_count: number;
  force_resend: boolean;
}

export interface ScheduledJobLastRun {
  job_key: string;
  cron_enabled?: boolean;
  has_run: boolean;
  last_run_at?: string | null;
  started_at?: string | null;
  status?: string;
  sent_count?: number;
  skipped_count?: number;
  failed_count?: number;
  week_start?: string | null;
  trigger_source?: string;
  error_message?: string | null;
}

export const TENANT_ROLLUP_EMAIL_JOB_KEY = 'tenant_enquiry_recommendation_rollup';

export interface TenantRollupEmailSchedule {
  active: boolean;
  platform_enabled: boolean;
  opt_in: boolean;
  cron_enabled: boolean;
  sent_this_week: boolean;
  current_week_start: string;
  next_scheduled_at: string | null;
}

export interface ScheduledJobRunLogRow {
  id: string;
  job_key: string;
  started_at: string;
  completed_at: string | null;
  status: string;
  sent_count: number;
  skipped_count: number;
  week_start: string | null;
  trigger_source: string;
  error_message: string | null;
  details: Record<string, unknown> | null;
}

export interface TenantRollupEmailFailureLogRow {
  id: string;
  organization_id: string;
  organization_name: string;
  week_start: string;
  error_message: string;
  failed_at: string;
  trigger_source: string;
}

export interface TenantRollupEmailRetryRequestRow {
  id: string;
  organization_id: string;
  organization_name: string;
  week_start: string;
  requested_at: string;
  requested_by_user_id: string | null;
}

export interface TenantRollupEmailLatestFailure {
  has_failure: boolean;
  week_start?: string;
  failed_at?: string | null;
  error_message?: string | null;
  sent_this_week?: boolean;
  retry_requested_this_week?: boolean;
}

export interface AbWinnerApplyResult {
  applied: boolean;
  winner?: string;
  applied_max_items?: number;
  evaluation?: AbWinnerEvaluation;
  error?: string;
}

const LOOKBACK_DAYS = 30;

export function countRecommendationLinesInWhatsAppBlock(block: string | null | undefined): number {
  if (!block?.trim()) return 0;
  return block.split('\n').filter((line) => line.trim().startsWith('•')).length;
}

function computeAttachRate(withChips: number, total: number): number {
  return total ? (withChips / total) * 100 : 0;
}

export function rollupAbVariantShares(
  rows: Array<{
    event_type: string;
    recommendation_line_count?: number | null;
    ab_variant?: string | null;
  }>,
): Pick<
  EnquiryRecommendationAnalyticsSummary,
  'variantAShares' | 'variantBShares' | 'variantAAttachRate' | 'variantBAttachRate'
> {
  let variantAWithChips = 0;
  let variantBWithChips = 0;
  let variantAShares = 0;
  let variantBShares = 0;

  for (const row of rows) {
    if (row.event_type !== 'whatsapp_share') continue;
    const withChips = (row.recommendation_line_count ?? 0) > 0;
    if (row.ab_variant === 'B') {
      variantBShares += 1;
      if (withChips) variantBWithChips += 1;
    } else {
      variantAShares += 1;
      if (withChips) variantAWithChips += 1;
    }
  }

  return {
    variantAShares,
    variantBShares,
    variantAAttachRate: computeAttachRate(variantAWithChips, variantAShares),
    variantBAttachRate: computeAttachRate(variantBWithChips, variantBShares),
  };
}

export function rollupWhatsAppConversionStats(
  rows: Array<{ event_type: string; enquiry_converted_at?: string | null }>,
): Pick<EnquiryRecommendationAnalyticsSummary, 'totalShares' | 'convertedShares' | 'conversionRate'> {
  let totalShares = 0;
  let convertedShares = 0;

  for (const row of rows) {
    if (row.event_type !== 'whatsapp_share') continue;
    totalShares += 1;
    if (row.enquiry_converted_at) convertedShares += 1;
  }

  return {
    totalShares,
    convertedShares,
    conversionRate: computeAttachRate(convertedShares, totalShares),
  };
}

/** Fire-and-forget analytics insert — never blocks enquiry workflows. */
export async function logEnquiryRecommendationEvent(input: EnquiryRecommendationEventInput): Promise<void> {
  const orgId = input.organizationId?.trim();
  const enquiryId = input.enquiryId?.trim();
  if (!orgId || !enquiryId) return;

  try {
    const { data: auth } = await supabase.auth.getUser();
    await supabase.from('enquiry_recommendation_share_events').insert({
      organization_id: orgId,
      enquiry_id: enquiryId,
      event_type: input.eventType,
      recommendation_line_count: Math.max(0, Math.floor(input.recommendationLineCount)),
      ab_variant: input.abVariant ?? null,
      actor_user_id: auth.user?.id ?? null,
    });
  } catch (error) {
    console.warn('Enquiry recommendation analytics insert failed:', error);
  }
}

export async function fetchEnquiryRecommendationAnalyticsSummary(
  organizationId?: string | null,
  lookbackDays = LOOKBACK_DAYS,
): Promise<EnquiryRecommendationAnalyticsSummary> {
  const empty: EnquiryRecommendationAnalyticsSummary = {
    totalShares: 0,
    sharesWithChips: 0,
    sharesWithoutChips: 0,
    adminCopies: 0,
    attachRate: 0,
    convertedShares: 0,
    conversionRate: 0,
    variantAShares: 0,
    variantBShares: 0,
    variantAAttachRate: 0,
    variantBAttachRate: 0,
  };

  const orgId = organizationId?.trim();
  if (!orgId) return empty;

  const since = new Date();
  since.setDate(since.getDate() - lookbackDays);

  const { data, error } = await supabase
    .from('enquiry_recommendation_share_events')
    .select('event_type, recommendation_line_count, ab_variant, enquiry_converted_at')
    .eq('organization_id', orgId)
    .gte('created_at', since.toISOString());

  if (error || !data?.length) return empty;

  let sharesWithChips = 0;
  let sharesWithoutChips = 0;
  let adminCopies = 0;
  let convertedShares = 0;

  for (const row of data) {
    if (row.event_type === 'admin_copy') {
      adminCopies += 1;
      continue;
    }
    if (row.event_type === 'whatsapp_share') {
      if ((row.recommendation_line_count ?? 0) > 0) {
        sharesWithChips += 1;
      } else {
        sharesWithoutChips += 1;
      }
      if (row.enquiry_converted_at) {
        convertedShares += 1;
      }
    }
  }

  const totalShares = sharesWithChips + sharesWithoutChips;
  const abStats = rollupAbVariantShares(data);

  return {
    totalShares,
    sharesWithChips,
    sharesWithoutChips,
    adminCopies,
    attachRate: computeAttachRate(sharesWithChips, totalShares),
    convertedShares,
    conversionRate: computeAttachRate(convertedShares, totalShares),
    ...abStats,
  };
}

export async function fetchPlatformEnquiryRecommendationRollup(
  lookbackDays = LOOKBACK_DAYS,
): Promise<PlatformEnquiryRecommendationRollup | null> {
  try {
    const { data, error } = await supabase.rpc('saas_rollup_enquiry_recommendation_share_events', {
      p_lookback_days: lookbackDays,
    });
    if (error || !data) return null;
    const rollup = data as PlatformEnquiryRecommendationRollup;
    return {
      lookback_days: rollup.lookback_days ?? lookbackDays,
      total_shares: rollup.total_shares ?? 0,
      shares_with_chips: rollup.shares_with_chips ?? 0,
      shares_without_chips: rollup.shares_without_chips ?? 0,
      admin_copies: rollup.admin_copies ?? 0,
      variant_a_events: rollup.variant_a_events ?? 0,
      variant_b_events: rollup.variant_b_events ?? 0,
      variant_a_shares: rollup.variant_a_shares ?? 0,
      variant_b_shares: rollup.variant_b_shares ?? 0,
      variant_a_with_chips: rollup.variant_a_with_chips ?? 0,
      variant_b_with_chips: rollup.variant_b_with_chips ?? 0,
      variant_a_attach_rate: rollup.variant_a_attach_rate ?? 0,
      variant_b_attach_rate: rollup.variant_b_attach_rate ?? 0,
      converted_shares: rollup.converted_shares ?? 0,
      conversion_rate: rollup.conversion_rate ?? 0,
      orgs: Array.isArray(rollup.orgs) ? rollup.orgs : [],
    };
  } catch (error) {
    console.warn('Platform enquiry recommendation rollup failed:', error);
    return null;
  }
}

export async function exportPlatformEnquiryRecommendationEvents(
  since?: string | null,
  until?: string | null,
  limit = 10000,
): Promise<EnquiryRecommendationExportRow[]> {
  const sinceIso = since ? new Date(`${since}T00:00:00`).toISOString() : null;
  const untilIso = until ? new Date(`${until}T23:59:59`).toISOString() : null;

  const { data, error } = await supabase.rpc('saas_export_enquiry_recommendation_share_events', {
    p_since: sinceIso,
    p_until: untilIso,
    p_limit: limit,
  });

  if (error) {
    console.warn('Enquiry recommendation export failed:', error);
    return [];
  }

  return (data as EnquiryRecommendationExportRow[]) ?? [];
}

/** Pure helper — mirrors SQL evaluate_enquiry_recommendation_ab_winner_internal. */
export function evaluateAbWinnerFromStats(
  variantAShares: number,
  variantBShares: number,
  variantAAttachRate: number,
  variantBAttachRate: number,
  variantAMaxItems: number,
  variantBMaxItems: number,
  minDeltaPct: number,
  minVariantShares: number,
  lookbackDays = LOOKBACK_DAYS,
): AbWinnerEvaluation {
  const minDelta = Math.min(100, Math.max(0, Math.floor(minDeltaPct)));
  const minShares = Math.max(1, Math.floor(minVariantShares));
  const base: AbWinnerEvaluation = {
    lookback_days: lookbackDays,
    eligible: false,
    winner: 'none',
    reason: 'inconclusive',
    recommended_max_items: variantAMaxItems,
    variant_a_shares: variantAShares,
    variant_b_shares: variantBShares,
    variant_a_attach_rate: variantAAttachRate,
    variant_b_attach_rate: variantBAttachRate,
    min_delta_pct: minDelta,
    min_variant_shares: minShares,
    variant_a_max_items: variantAMaxItems,
    variant_b_max_items: variantBMaxItems,
  };

  if (variantAShares < minShares || variantBShares < minShares) {
    return { ...base, reason: 'insufficient_shares' };
  }

  base.eligible = true;
  if (variantBAttachRate >= variantAAttachRate + minDelta) {
    return {
      ...base,
      winner: 'B',
      reason: 'variant_b_wins',
      recommended_max_items: variantBMaxItems,
    };
  }
  if (variantAAttachRate >= variantBAttachRate + minDelta) {
    return {
      ...base,
      winner: 'A',
      reason: 'variant_a_wins',
      recommended_max_items: variantAMaxItems,
    };
  }
  return { ...base, reason: 'delta_below_threshold' };
}

export async function fetchAbWinnerEvaluation(
  lookbackDays = LOOKBACK_DAYS,
): Promise<AbWinnerEvaluation | null> {
  try {
    const { data, error } = await supabase.rpc('saas_evaluate_enquiry_recommendation_ab_winner', {
      p_lookback_days: lookbackDays,
    });
    if (error || !data) return null;
    return data as AbWinnerEvaluation;
  } catch (error) {
    console.warn('AB winner evaluation failed:', error);
    return null;
  }
}

export async function applyAbWinner(
  lookbackDays = LOOKBACK_DAYS,
  force = false,
): Promise<AbWinnerApplyResult | null> {
  try {
    const { data, error } = await supabase.rpc('saas_apply_enquiry_recommendation_ab_winner', {
      p_lookback_days: lookbackDays,
      p_force: force,
    });
    if (error || !data) return null;
    return data as AbWinnerApplyResult;
  } catch (error) {
    console.warn('AB winner apply failed:', error);
    return null;
  }
}

export async function exportTenantEnquiryRecommendationEvents(
  since?: string | null,
  until?: string | null,
  limit = 5000,
): Promise<EnquiryRecommendationExportRow[]> {
  const sinceIso = since ? new Date(`${since}T00:00:00`).toISOString() : null;
  const untilIso = until ? new Date(`${until}T23:59:59`).toISOString() : null;

  const { data, error } = await supabase.rpc('export_enquiry_recommendation_share_events', {
    p_since: sinceIso,
    p_until: untilIso,
    p_limit: limit,
  });

  if (error) {
    console.warn('Tenant enquiry recommendation export failed:', error);
    return [];
  }

  return (data as EnquiryRecommendationExportRow[]) ?? [];
}

export async function fetchTenantWeeklyAttachRollup(
  lookbackDays = 90,
): Promise<EnquiryRecommendationWeeklyBucket[]> {
  try {
    const { data, error } = await supabase.rpc('rollup_enquiry_recommendation_attach_by_week', {
      p_lookback_days: lookbackDays,
    });
    if (error || !data) return [];
    return Array.isArray(data) ? (data as EnquiryRecommendationWeeklyBucket[]) : [];
  } catch (error) {
    console.warn('Tenant weekly attach rollup failed:', error);
    return [];
  }
}

export async function backfillEnquiryRecommendationShareConversions(
  organizationId?: string | null,
): Promise<BackfillShareConversionsResult | null> {
  try {
    const { data, error } = await supabase.rpc('saas_backfill_enquiry_recommendation_share_conversions', {
      p_organization_id: organizationId?.trim() || null,
    });
    if (error || !data) return null;
    const result = data as BackfillShareConversionsResult;
    return {
      updated: result.updated ?? 0,
      organization_id: result.organization_id,
    };
  } catch (error) {
    console.warn('Share conversion backfill failed:', error);
    return null;
  }
}

/** Tooltip for per-org backfill button after a run completes. */
export function formatOrgBackfillTooltip(organizationId: string, results: Record<string, number>): string {
  const count = results[organizationId];
  if (count === undefined) {
    return 'Backfill conversions for this organization';
  }
  if (count === 0) {
    return 'Last backfill: no shares needed updating';
  }
  return `Last backfill: marked ${count} share(s) as converted`;
}

export async function fetchTenantRollupEmailOptIns(): Promise<TenantRollupEmailOptInRollup> {
  const empty: TenantRollupEmailOptInRollup = {
    opt_in_count: 0,
    failures_this_week_count: 0,
    retry_requests_this_week_count: 0,
    orgs: [],
  };
  try {
    const { data, error } = await supabase.rpc('saas_list_enquiry_tenant_rollup_email_opt_ins');
    if (error || !data) return empty;
    const row = data as TenantRollupEmailOptInRollup;
    return {
      opt_in_count: Number(row.opt_in_count ?? 0),
      failures_this_week_count: Number(row.failures_this_week_count ?? 0),
      retry_requests_this_week_count: Number(row.retry_requests_this_week_count ?? 0),
      orgs: Array.isArray(row.orgs) ? row.orgs : [],
    };
  } catch (error) {
    console.warn('Tenant rollup opt-in list failed:', error);
    return empty;
  }
}

export async function fetchTenantRollupEmailOptInCount(): Promise<number> {
  const rollup = await fetchTenantRollupEmailOptIns();
  return rollup.opt_in_count;
}

export async function fetchTenantRollupEmailLastSent(): Promise<string | null> {
  try {
    const { data, error } = await supabase.rpc('get_tenant_enquiry_recommendation_rollup_email_last_sent');
    if (error || !data) return null;
    return typeof data === 'string' ? data : null;
  } catch (error) {
    console.warn('Tenant rollup email last sent failed:', error);
    return null;
  }
}

export async function fetchTenantRollupEmailHistory(limit = 12): Promise<TenantRollupEmailHistoryRow[]> {
  try {
    const { data, error } = await supabase.rpc('list_tenant_enquiry_rollup_email_history', {
      p_limit: limit,
    });
    if (error || !data) return [];
    return Array.isArray(data) ? (data as TenantRollupEmailHistoryRow[]) : [];
  } catch (error) {
    console.warn('Tenant rollup email history failed:', error);
    return [];
  }
}

export async function exportTenantRollupEmailHistory(limit = 52): Promise<TenantRollupEmailHistoryRow[]> {
  try {
    const { data, error } = await supabase.rpc('export_tenant_enquiry_rollup_email_history', {
      p_limit: limit,
    });
    if (error || !data) return [];
    return Array.isArray(data) ? (data as TenantRollupEmailHistoryRow[]) : [];
  } catch (error) {
    console.warn('Tenant rollup email history export failed:', error);
    return [];
  }
}

export async function fetchMissedTenantRollupEmailsThisWeek(): Promise<TenantRollupEmailMissedRollup> {
  const empty: TenantRollupEmailMissedRollup = { missed_count: 0, week_start: '' };
  try {
    const { data, error } = await supabase.rpc('saas_count_missed_tenant_rollup_emails_this_week');
    if (error || !data) return empty;
    const row = data as TenantRollupEmailMissedRollup;
    return {
      missed_count: Number(row.missed_count ?? 0),
      week_start: row.week_start ?? '',
    };
  } catch (error) {
    console.warn('Missed tenant rollup email count failed:', error);
    return empty;
  }
}

export async function fetchPendingTenantRollupRetryRequestsThisWeek(): Promise<TenantRollupEmailPendingRetryRollup> {
  const empty: TenantRollupEmailPendingRetryRollup = { pending_count: 0, week_start: '' };
  try {
    const { data, error } = await supabase.rpc('saas_count_pending_tenant_rollup_retry_requests_this_week');
    if (error || !data) return empty;
    const row = data as TenantRollupEmailPendingRetryRollup;
    return {
      pending_count: Number(row.pending_count ?? 0),
      week_start: row.week_start ?? '',
    };
  } catch (error) {
    console.warn('Pending tenant rollup retry count failed:', error);
    return empty;
  }
}

export async function fetchTenantRollupEmailRetryStatus(): Promise<TenantRollupEmailRetryStatus | null> {
  try {
    const { data, error } = await supabase.rpc('get_tenant_enquiry_rollup_email_retry_status');
    if (error || !data) return null;
    return data as TenantRollupEmailRetryStatus;
  } catch (error) {
    console.warn('Tenant rollup email retry status failed:', error);
    return null;
  }
}

export async function sendMissedTenantRollupEmails(): Promise<TenantRollupEmailManualResult | null> {
  try {
    const { data, error } = await supabase.functions.invoke('send-tenant-enquiry-recommendation-rollup', {
      body: { missed_only: true },
    });
    if (error) {
      console.warn('Missed tenant rollup email send failed:', error);
      return { error: error.message };
    }
    return (data as TenantRollupEmailManualResult) ?? null;
  } catch (error) {
    console.warn('Missed tenant rollup email send failed:', error);
    return {
      error: error instanceof Error ? error.message : 'Bulk send failed',
    };
  }
}

export async function sendPendingTenantRollupRetryEmails(): Promise<TenantRollupEmailManualResult | null> {
  try {
    const { data, error } = await supabase.functions.invoke('send-tenant-enquiry-recommendation-rollup', {
      body: { retry_requests_only: true },
    });
    if (error) {
      console.warn('Pending tenant rollup retry send failed:', error);
      return { error: error.message };
    }
    return (data as TenantRollupEmailManualResult) ?? null;
  } catch (error) {
    console.warn('Pending tenant rollup retry send failed:', error);
    return {
      error: error instanceof Error ? error.message : 'Bulk retry send failed',
    };
  }
}

export async function notifyPlatformAdminsRollupBulkSend(
  sent: number,
  skipped: number,
  weekStart?: string | null,
  failed?: number,
): Promise<number> {
  try {
    const { data, error } = await supabase.rpc('saas_notify_platform_admins_rollup_bulk_send', {
      p_sent: sent,
      p_skipped: skipped,
      p_week_start: weekStart ?? null,
      p_failed: failed ?? 0,
    });
    if (error) {
      console.warn('Platform rollup bulk notify failed:', error);
      return 0;
    }
    return Number(data ?? 0);
  } catch (error) {
    console.warn('Platform rollup bulk notify failed:', error);
    return 0;
  }
}

export async function notifyPlatformAdminsRollupRetryBulkSend(
  sent: number,
  skipped: number,
  weekStart?: string | null,
  failed?: number,
): Promise<number> {
  try {
    const { data, error } = await supabase.rpc('saas_notify_platform_admins_rollup_retry_bulk_send', {
      p_sent: sent,
      p_skipped: skipped,
      p_week_start: weekStart ?? null,
      p_failed: failed ?? 0,
    });
    if (error) {
      console.warn('Platform rollup retry bulk notify failed:', error);
      return 0;
    }
    return Number(data ?? 0);
  } catch (error) {
    console.warn('Platform rollup retry bulk notify failed:', error);
    return 0;
  }
}

export async function countTenantRollupChimeOverrides(): Promise<number> {
  try {
    const { data, error } = await supabase.rpc('saas_count_tenant_rollup_chime_overrides');
    if (error) {
      console.warn('Count tenant rollup chime overrides failed:', error);
      return 0;
    }
    return Number(data ?? 0);
  } catch (error) {
    console.warn('Count tenant rollup chime overrides failed:', error);
    return 0;
  }
}

export async function clearTenantRollupChimeOverrides(): Promise<number> {
  try {
    const { data, error } = await supabase.rpc('saas_clear_tenant_rollup_chime_overrides');
    if (error) {
      console.warn('Clear tenant rollup chime overrides failed:', error);
      return 0;
    }
    return Number(data ?? 0);
  } catch (error) {
    console.warn('Clear tenant rollup chime overrides failed:', error);
    return 0;
  }
}

export async function fetchTenantRollupEmailCronStatus(): Promise<ScheduledJobLastRun | null> {
  try {
    const { data, error } = await supabase.rpc('saas_get_scheduled_job_last_run', {
      p_job_key: TENANT_ROLLUP_EMAIL_JOB_KEY,
    });
    if (error || !data) return null;
    return data as ScheduledJobLastRun;
  } catch (error) {
    console.warn('Tenant rollup cron status failed:', error);
    return null;
  }
}

export async function fetchTenantRollupEmailSchedule(): Promise<TenantRollupEmailSchedule | null> {
  try {
    const { data, error } = await supabase.rpc('get_tenant_enquiry_rollup_email_schedule');
    if (error || !data) return null;
    return data as TenantRollupEmailSchedule;
  } catch (error) {
    console.warn('Tenant rollup email schedule failed:', error);
    return null;
  }
}

export async function exportScheduledJobRunLog(
  jobKey?: string | null,
  since?: string | null,
  until?: string | null,
  limit = 5000,
  triggerSource?: string | null,
): Promise<ScheduledJobRunLogRow[]> {
  const sinceIso = since ? new Date(`${since}T00:00:00`).toISOString() : null;
  const untilIso = until ? new Date(`${until}T23:59:59`).toISOString() : null;

  const { data, error } = await supabase.rpc('saas_export_scheduled_job_run_log', {
    p_job_key: jobKey?.trim() || null,
    p_since: sinceIso,
    p_until: untilIso,
    p_limit: limit,
    p_trigger_source: triggerSource?.trim() || null,
  });

  if (error) {
    console.warn('Scheduled job run export failed:', error);
    return [];
  }

  return (data as ScheduledJobRunLogRow[]) ?? [];
}

export async function fetchScheduledJobRunList(
  jobKey?: string | null,
  limit = 25,
  triggerSource?: string | null,
): Promise<ScheduledJobRunLogRow[]> {
  try {
    const { data, error } = await supabase.rpc('saas_list_scheduled_job_runs', {
      p_job_key: jobKey?.trim() || null,
      p_limit: limit,
      p_trigger_source: triggerSource?.trim() || null,
    });
    if (error || !data) return [];
    return Array.isArray(data) ? (data as ScheduledJobRunLogRow[]) : [];
  } catch (error) {
    console.warn('Scheduled job run list failed:', error);
    return [];
  }
}

export async function fetchTenantRollupEmailFailureLog(
  limit = 25,
): Promise<TenantRollupEmailFailureLogRow[]> {
  try {
    const { data, error } = await supabase.rpc('saas_list_tenant_rollup_email_failures', {
      p_limit: limit,
    });
    if (error || !data) return [];
    return Array.isArray(data) ? (data as TenantRollupEmailFailureLogRow[]) : [];
  } catch (error) {
    console.warn('Tenant rollup failure log failed:', error);
    return [];
  }
}

export async function exportTenantRollupEmailFailureLog(
  since?: string | null,
  until?: string | null,
  limit = 5000,
  triggerSource?: TenantRollupCronTriggerFilter | null,
): Promise<TenantRollupEmailFailureLogRow[]> {
  const sinceIso = since ? new Date(`${since}T00:00:00`).toISOString() : null;
  const untilIso = until ? new Date(`${until}T23:59:59`).toISOString() : null;
  const triggerParam =
    triggerSource && triggerSource !== 'all' ? tenantRollupCronTriggerFilterParam(triggerSource) : null;

  const { data, error } = await supabase.rpc('saas_export_tenant_rollup_email_failure_log', {
    p_since: sinceIso,
    p_until: untilIso,
    p_limit: limit,
    p_trigger_source: triggerParam,
  });

  if (error) {
    console.warn('Tenant rollup failure log export failed:', error);
    return [];
  }

  return (data as TenantRollupEmailFailureLogRow[]) ?? [];
}

export async function fetchTenantRollupEmailLatestFailure(): Promise<TenantRollupEmailLatestFailure | null> {
  try {
    const { data, error } = await supabase.rpc('get_tenant_enquiry_rollup_email_latest_failure');
    if (error || !data) return null;
    return data as TenantRollupEmailLatestFailure;
  } catch (error) {
    console.warn('Tenant rollup email latest failure failed:', error);
    return null;
  }
}

export interface TenantRollupEmailRetryRequestResult {
  requested?: boolean;
  reason?: string;
  week_start?: string;
  retry_requested_this_week?: boolean;
  auto_retry_triggered?: boolean;
}

export async function requestTenantRollupEmailRetry(): Promise<TenantRollupEmailRetryRequestResult | null> {
  try {
    const { data, error } = await supabase.rpc('request_tenant_enquiry_rollup_email_retry');
    if (error) {
      console.warn('Tenant rollup email retry request failed:', error);
      return { requested: false, reason: error.message };
    }
    return (data as TenantRollupEmailRetryRequestResult) ?? null;
  } catch (error) {
    console.warn('Tenant rollup email retry request failed:', error);
    return {
      requested: false,
      reason: error instanceof Error ? error.message : 'Request failed',
    };
  }
}

export async function fetchTenantRollupEmailRetryRequests(
  limit = 25,
): Promise<TenantRollupEmailRetryRequestRow[]> {
  try {
    const { data, error } = await supabase.rpc('saas_list_tenant_rollup_email_retry_requests', {
      p_limit: limit,
    });
    if (error || !data) return [];
    return Array.isArray(data) ? (data as TenantRollupEmailRetryRequestRow[]) : [];
  } catch (error) {
    console.warn('Tenant rollup retry request list failed:', error);
    return [];
  }
}

export async function exportTenantRollupEmailRetryRequests(
  since?: string | null,
  until?: string | null,
  limit = 5000,
): Promise<TenantRollupEmailRetryRequestRow[]> {
  const sinceIso = since ? new Date(`${since}T00:00:00`).toISOString() : null;
  const untilIso = until ? new Date(`${until}T23:59:59`).toISOString() : null;

  const { data, error } = await supabase.rpc('saas_export_tenant_rollup_email_retry_requests', {
    p_since: sinceIso,
    p_until: untilIso,
    p_limit: limit,
  });

  if (error) {
    console.warn('Tenant rollup retry request export failed:', error);
    return [];
  }

  return (data as TenantRollupEmailRetryRequestRow[]) ?? [];
}

export async function notifyPlatformAdminsRollupEmailFailures(
  failedCount: number,
  weekStart?: string | null,
  sampleError?: string | null,
): Promise<number> {
  try {
    const { data, error } = await supabase.rpc('saas_notify_platform_admins_rollup_email_failures', {
      p_failed_count: failedCount,
      p_week_start: weekStart ?? null,
      p_sample_error: sampleError ?? null,
    });
    if (error) {
      console.warn('Platform rollup failure notify failed:', error);
      return 0;
    }
    return Number(data ?? 0);
  } catch (error) {
    console.warn('Platform rollup failure notify failed:', error);
    return 0;
  }
}

export async function fetchTenantRollupEmailResendLog(limit = 25): Promise<TenantRollupEmailResendLogRow[]> {
  try {
    const { data, error } = await supabase.rpc('saas_list_tenant_rollup_email_resends', {
      p_limit: limit,
    });
    if (error || !data) return [];
    return Array.isArray(data) ? (data as TenantRollupEmailResendLogRow[]) : [];
  } catch (error) {
    console.warn('Tenant rollup resend log failed:', error);
    return [];
  }
}

export async function exportTenantRollupEmailResendLog(
  since?: string | null,
  until?: string | null,
  limit = 5000,
): Promise<TenantRollupEmailResendLogRow[]> {
  const sinceIso = since ? new Date(`${since}T00:00:00`).toISOString() : null;
  const untilIso = until ? new Date(`${until}T23:59:59`).toISOString() : null;

  const { data, error } = await supabase.rpc('saas_export_tenant_rollup_email_resend_log', {
    p_since: sinceIso,
    p_until: untilIso,
    p_limit: limit,
  });

  if (error) {
    console.warn('Tenant rollup resend log export failed:', error);
    return [];
  }

  return (data as TenantRollupEmailResendLogRow[]) ?? [];
}

export async function previewPlatformTenantRollupEmail(
  organizationId: string,
): Promise<TenantRollupEmailManualResult | null> {
  try {
    const { data, error } = await supabase.functions.invoke('send-tenant-enquiry-recommendation-rollup', {
      body: { organization_id: organizationId, preview_only: true },
    });
    if (error) {
      console.warn('Tenant rollup email preview failed:', error);
      return { error: error.message };
    }
    return (data as TenantRollupEmailManualResult) ?? null;
  } catch (error) {
    console.warn('Tenant rollup email preview failed:', error);
    return {
      error: error instanceof Error ? error.message : 'Preview failed',
    };
  }
}

export async function resendPlatformTenantRollupEmail(
  organizationId: string,
  force = true,
): Promise<TenantRollupEmailManualResult | null> {
  try {
    const { data, error } = await supabase.functions.invoke('send-tenant-enquiry-recommendation-rollup', {
      body: { organization_id: organizationId, force },
    });
    if (error) {
      console.warn('Tenant rollup email resend failed:', error);
      return { error: error.message };
    }
    return (data as TenantRollupEmailManualResult) ?? null;
  } catch (error) {
    console.warn('Tenant rollup email resend failed:', error);
    return {
      error: error instanceof Error ? error.message : 'Resend failed',
    };
  }
}

export async function exportPlatformEnquiryRollupEmailLogs(
  since?: string | null,
  until?: string | null,
  limit = 5000,
  scope: EnquiryRollupEmailLogScope = 'all',
): Promise<EnquiryRollupEmailLogExportRow[]> {
  const sinceIso = since ? new Date(`${since}T00:00:00`).toISOString() : null;
  const untilIso = until ? new Date(`${until}T23:59:59`).toISOString() : null;

  const { data, error } = await supabase.rpc('saas_export_enquiry_rollup_email_logs', {
    p_since: sinceIso,
    p_until: untilIso,
    p_limit: limit,
    p_scope: scope,
  });

  if (error) {
    console.warn('Enquiry rollup email log export failed:', error);
    return [];
  }

  return (data as EnquiryRollupEmailLogExportRow[]) ?? [];
}

export async function fetchPlatformWeeklyAttachRollup(
  lookbackDays = 90,
): Promise<EnquiryRecommendationWeeklyBucket[]> {
  try {
    const { data, error } = await supabase.rpc('saas_rollup_enquiry_recommendation_attach_by_week', {
      p_lookback_days: lookbackDays,
    });
    if (error || !data) return [];
    return Array.isArray(data) ? (data as EnquiryRecommendationWeeklyBucket[]) : [];
  } catch (error) {
    console.warn('Platform weekly attach rollup failed:', error);
    return [];
  }
}
